import React, { useEffect } from "react";
import { CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandShortcut, CommandSeparator } from "@/components/ui/command";

/**
 * Global ⌘K / Ctrl-K command palette.
 * Consumers pass a list of `commands` describing everything the user might do
 * (Preview, Publish, Export as PNG, Refresh, Open chat, …). The palette handles
 * filtering, grouping, keyboard nav and shortcut hints.
 *
 * Command shape:
 *   { id, label, group?, keywords?, shortcut?, icon?, hidden?, action() }
 */
export function CommandPalette({ open, onOpenChange, commands }) {
  // Global Ctrl/Cmd+K toggle
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onOpenChange]);

  // Group by `group`, preserve insertion order.
  const groups = [];
  const map = new Map();
  for (const c of commands || []) {
    if (c.hidden) continue;
    const g = c.group || "Actions";
    if (!map.has(g)) { map.set(g, []); groups.push(g); }
    map.get(g).push(c);
  }

  const run = (cmd) => {
    onOpenChange(false);
    // Slight delay so the dialog can close before triggering nav / other dialogs
    setTimeout(() => { try { cmd.action(); } catch (e) { /* ignore */ } }, 30);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Type a command or search…" data-testid="cmd-palette-input" />
      <CommandList data-testid="cmd-palette-list">
        <CommandEmpty>No matching action.</CommandEmpty>
        {groups.map((g, gi) => (
          <React.Fragment key={g}>
            {gi > 0 && <CommandSeparator />}
            <CommandGroup heading={g}>
              {map.get(g).map((cmd) => (
                <CommandItem
                  key={cmd.id}
                  value={`${cmd.label} ${(cmd.keywords || []).join(" ")}`}
                  data-testid={`cmd-item-${cmd.id}`}
                  onSelect={() => run(cmd)}
                >
                  {cmd.icon ? <cmd.icon className="w-4 h-4 text-[color:var(--brand-ocean)]" /> : null}
                  <span className="flex-1 truncate">{cmd.label}</span>
                  {cmd.shortcut ? <CommandShortcut>{cmd.shortcut}</CommandShortcut> : null}
                </CommandItem>
              ))}
            </CommandGroup>
          </React.Fragment>
        ))}
      </CommandList>
    </CommandDialog>
  );
}
